import * as React from 'react';
import { AlertTriangle, ChevronRight, Sparkles, Users } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip } from '@/components/ui/status-chip';
import {
  AutosaveField,
  Callout,
  CodeBadge,
  DetailCard,
  DetailHead,
  RecordRow,
  WorkspaceButton,
  WorkspaceSelect,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import {
  mergePerson,
  resolveDuplicate,
  retirePerson,
  undoMerge,
  updatePerson,
  useDiscoveryWrite,
  usePeopleQuery,
  usePersonQuery,
  type PersonDetail,
  type PersonPatch,
} from '@/lib/api/discovery-sessions';
import { PersonAvatar } from '../coverage-bits';
import { plural } from '../discovery-logic';
import { errorText } from '../sessions/errors';
import { firstName, longDay, statusChip } from '../sessions/sessions-logic';

/**
 * A person, opened from the People list in Sessions (E12-F03, Decisions
 * 54.10–54.12): their interviews, answers and evidence, edited in place.
 * A possible duplicate is resolved here (merge or different person), a
 * merge can be undone, and a person with nothing attached can be retired.
 * There is no prototype screen for it; it follows `personDetail` in
 * `docs/discovery/prototype/discovery.js` and the list-and-detail pattern.
 */

const SOURCE: Record<string, string> = {
  calendar: 'came from a calendar invite',
  transcript: 'came from a transcript',
  link_respondent: 'came from a secure link response',
};
const MATCH: Record<string, string> = {
  email: 'Same email address',
  name: 'Same first and last name',
  nickname: 'Same last name and a known nickname',
  initial: 'Same last name and the same first initial',
};

/** Saves on leaving the field ("Saved when you leave the field"); not while typing. */
const ON_BLUR = 10 * 60_000;

export function PersonDetailView({
  engagementId,
  personId,
  onOpenInterview,
  onOpenPerson,
}: {
  engagementId: string;
  personId: string;
  onOpenInterview: (id: string) => void;
  onOpenPerson: (id: string) => void;
}) {
  const { toast } = useToast();
  const q = usePersonQuery(engagementId, personId);
  const fail = (e: unknown) => toast({ message: errorText(e), tone: 'critical' });
  const edit = useDiscoveryWrite(engagementId, (body: PersonPatch) =>
    updatePerson(engagementId, personId, body),
  );
  const resolve = useDiscoveryWrite(engagementId, (decision: 'merge' | 'different') =>
    resolveDuplicate(engagementId, personId, decision),
  );
  const undo = useDiscoveryWrite(engagementId, (mergeId: string) =>
    undoMerge(engagementId, mergeId),
  );
  const retire = useDiscoveryWrite(engagementId, (action: 'retire' | 'restore') =>
    retirePerson(engagementId, personId, action),
  );
  const merge = useDiscoveryWrite(engagementId, (intoId: string) =>
    mergePerson(engagementId, personId, intoId),
  );
  const people = usePeopleQuery(engagementId);
  const [into, setInto] = React.useState('');

  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading the person…
      </div>
    );
  }
  const d = q.data;
  const p = d.person;
  const retired = p.status === 'retired';
  const twin = d.possible_duplicate_of;
  const kept = d.merges.filter((m) => m.kept_person.id === p.id && !m.undone_at);
  const answers = d.answers.filter((x) => !x.superseded);
  const other = d.evidence.filter((e) => e.channel !== 'questionnaire' && e.status !== 'proposed');
  const waiting = d.evidence.filter((e) => e.status === 'proposed');

  const save = (field: keyof PersonPatch) => (value: string) =>
    edit.mutate({ [field]: value || null }, { onError: fail });

  const undoMergeOf = (mergeId: string, name: string) =>
    undo.mutate(mergeId, {
      onSuccess: () =>
        toast({
          message: `Merge undone. Everything that moved went back to “${name}”, and coverage was counted again.`,
        }),
      onError: fail,
    });

  return (
    <div>
      <DetailHead
        leading={<PersonAvatar id={p.id} name={p.full_name} size={40} />}
        title={p.full_name}
        meta={[
          `${p.title || 'No title yet'}${p.department ? `, ${p.department}` : ''}`,
          SOURCE[p.source],
        ]
          .filter(Boolean)
          .join(' · ')}
        trailing={
          retired ? (
            <StatusChip tone="frozen">Retired</StatusChip>
          ) : twin ? (
            <StatusChip tone="at-risk">Possible duplicate</StatusChip>
          ) : null
        }
      />
      {twin && !retired ? (
        <Callout
          tone="warn"
          icon={AlertTriangle}
          className="mb-4"
          actions={
            <>
              <WorkspaceButton
                disabled={resolve.isPending}
                onClick={() =>
                  resolve.mutate('merge', {
                    onSuccess: (r) => {
                      toast({
                        message: `Merged “${p.full_name}” into ${twin.full_name}. Everything they say counts as one source.`,
                        actions: r.merge_id
                          ? [
                              {
                                label: 'Undo',
                                onClick: () => undoMergeOf(r.merge_id!, p.full_name),
                              },
                            ]
                          : undefined,
                      });
                      onOpenPerson(twin.id);
                    },
                    onError: fail,
                  })
                }
              >
                Merge into {firstName(twin.full_name)}
              </WorkspaceButton>
              <WorkspaceButton
                variant="tertiary"
                disabled={resolve.isPending}
                onClick={() =>
                  resolve.mutate('different', {
                    onSuccess: () =>
                      toast({
                        message: `Recorded: “${p.full_name}” and ${twin.full_name} are different people. ${firstName(p.full_name)}'s evidence can be confirmed as a source of their own.`,
                      }),
                    onError: fail,
                  })
                }
              >
                Different person
              </WorkspaceButton>
            </>
          }
        >
          Possible duplicate of <b>{twin.full_name}</b>
          {twin.title ? `, ${twin.title}` : ''}.{twin.match ? ` ${MATCH[twin.match] ?? ''}.` : ''}{' '}
          Until you answer, evidence from this record can’t be confirmed.
        </Callout>
      ) : null}
      {kept.map((m) => (
        <Callout
          key={m.id}
          tone="tip"
          icon={Users}
          className="mb-4 mt-0"
          actions={
            m.undoable ? (
              <button
                type="button"
                className={linkishClass()}
                disabled={undo.isPending}
                onClick={() => undoMergeOf(m.id, m.merged_person.full_name)}
              >
                Undo the merge
              </button>
            ) : null
          }
        >
          “{m.merged_person.full_name}” was merged into this record
          {m.merged_by_name ? ` by ${m.merged_by_name}` : ''} on {longDay(m.merged_at)}. Everything
          they said counts as one source.
        </Callout>
      ))}
      {retired ? (
        <Callout
          tone="note"
          className="mb-4"
          actions={
            <button
              type="button"
              className={linkishClass()}
              disabled={retire.isPending}
              onClick={() =>
                retire.mutate('restore', {
                  onSuccess: () => toast({ message: `${p.full_name} restored.` }),
                  onError: fail,
                })
              }
            >
              Restore
            </button>
          }
        >
          Retired{d.retired?.by_name ? ` by ${d.retired.by_name}` : ''}
          {d.retired?.at ? ` on ${longDay(d.retired.at)}` : ''}. Not offered in any list or picker.
          Nothing was deleted.
        </Callout>
      ) : null}

      <DetailCard className="block">
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <Field label="Title">
            <AutosaveField
              aria-label="Title"
              className="h-input-h"
              value={p.title ?? ''}
              placeholder="e.g. Accounts Payable Clerk"
              disabled={retired}
              delayMs={ON_BLUR}
              onSave={save('title')}
            />
          </Field>
          <Field label="Department">
            <AutosaveField
              aria-label="Department"
              className="h-input-h"
              value={p.department ?? ''}
              placeholder="e.g. Finance"
              disabled={retired}
              delayMs={ON_BLUR}
              onSave={save('department')}
            />
          </Field>
          <Field
            label={
              <>
                Email <span className="font-normal text-ink-500">for invites and secure links</span>
              </>
            }
          >
            <AutosaveField
              aria-label="Email"
              className="h-input-h"
              value={p.email ?? ''}
              placeholder="name@client.gov"
              disabled={retired}
              delayMs={ON_BLUR}
              onSave={save('email')}
            />
          </Field>
        </div>
        <div className="mt-2 text-meta text-ink-500">
          Saved when you leave the field. Each change is recorded with the old and new value, and
          invites and links use the new details from then on.
        </div>
      </DetailCard>

      <Section title="Interviews" n={d.interviews.length}>
        {d.interviews.length === 0 ? <None /> : null}
        {d.interviews.map((i) => {
          const chip = statusChip(i.state);
          return (
            <RecordRow key={i.id} onClick={() => onOpenInterview(i.id)}>
              <CodeBadge>{i.code}</CodeBadge>
              <span className="min-w-0 flex-1 text-ink-900">{i.title}</span>
              <StatusChip tone={chip.tone} size="sm">
                {chip.label}
              </StatusChip>
              <ChevronRight className="h-3.5 w-3.5 text-ink-500" aria-hidden="true" />
            </RecordRow>
          );
        })}
      </Section>
      <Section title="Questionnaire answers" n={answers.length}>
        {answers.length === 0 ? <None yet /> : null}
        <Said
          rows={answers.map((x) => ({
            id: x.id,
            text: x.prompt_as_asked,
            meta: `${x.questionnaire.code}${x.questionnaire.name ? ` ${x.questionnaire.name}` : ''} · answered ${longDay(x.answered_at)}`,
            number: x.evidence_number,
            gone: false,
          }))}
        />
      </Section>
      <Section title="Other evidence" n={other.length}>
        {other.length === 0 ? <None yet /> : null}
        <Said
          rows={other.map((e) => ({
            id: e.id,
            text: e.text,
            meta: citationText(e.citation),
            number: e.number,
            gone: e.status === 'withdrawn',
            untagged: e.area_ids.length === 0,
          }))}
        />
        {waiting.length > 0 ? (
          <Callout tone="ai" icon={Sparkles} className="mt-2.5">
            {plural(waiting.length, 'more item')} {waiting.length === 1 ? 'is' : 'are'} waiting for
            review.
          </Callout>
        ) : null}
      </Section>
      {retired || twin ? null : (
        <Section title="Merge">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="min-w-0 flex-1 text-body-sm text-ink-700">
              The same person as another record? Merge this one into it: everything moves to the
              kept record and counts as one source. It can be undone.
            </span>
            <WorkspaceSelect
              aria-label="Merge into"
              className="h-[30px] w-auto py-0 text-meta"
              value={into}
              onChange={(e) => setInto(e.target.value)}
            >
              <option value="">Pick the record to keep</option>
              {(people.data?.items ?? [])
                .filter((x) => x.id !== p.id && x.status !== 'retired' && !x.possible_duplicate_of)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.full_name}
                    {x.title ? `, ${x.title}` : ''}
                  </option>
                ))}
            </WorkspaceSelect>
            <WorkspaceButton
              disabled={!into || merge.isPending}
              onClick={() => {
                const kept = people.data?.items.find((x) => x.id === into);
                merge.mutate(into, {
                  onSuccess: (r) => {
                    toast({
                      message: `Merged “${p.full_name}” into ${kept?.full_name ?? 'the kept record'}. Everything they say counts as one source.`,
                      actions: [
                        { label: 'Undo', onClick: () => undoMergeOf(r.merge_id, p.full_name) },
                      ],
                    });
                    onOpenPerson(into);
                  },
                  onError: fail,
                });
              }}
            >
              Merge
            </WorkspaceButton>
          </div>
        </Section>
      )}
      {retired ? null : (
        <Section title="Retire">
          {d.can_retire ? (
            <div className="flex items-center gap-2.5">
              <span className="min-w-0 flex-1 text-body-sm text-ink-700">
                Nothing is attached to this record, so it can be retired. It leaves every list and
                picker, stays on record and can be restored.
              </span>
              <WorkspaceButton
                disabled={retire.isPending}
                onClick={() =>
                  retire.mutate('retire', {
                    onSuccess: () =>
                      toast({
                        message: `${p.full_name} retired. Not offered in any list or picker; nothing was deleted.`,
                        actions: [
                          {
                            label: 'Restore',
                            onClick: () => retire.mutate('restore', { onError: fail }),
                          },
                        ],
                      }),
                    onError: fail,
                  })
                }
              >
                Retire
              </WorkspaceButton>
            </div>
          ) : (
            <div className="text-meta text-ink-500">
              {firstName(p.full_name)} can’t be retired: this record has {attachedText(d)}. Only a
              person with nothing attached can be retired.
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block text-meta font-medium text-ink-700">{label}</span>
      {children}
    </label>
  );
}

function Section({ title, n, children }: { title: string; n?: number; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h3 className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase leading-[18px] tracking-[0.06em] text-ink-500">
        {title}
        {n !== undefined ? (
          <span className="font-mono font-normal tracking-normal">{n}</span>
        ) : null}
      </h3>
      {children}
    </section>
  );
}

function None({ yet = false }: { yet?: boolean }) {
  return <div className="text-meta text-ink-500">{yet ? 'None yet.' : 'None.'}</div>;
}

function Said({
  rows,
}: {
  rows: Array<{
    id: string;
    text: string;
    meta: string;
    number: string | null;
    gone: boolean;
    untagged?: boolean;
  }>;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
      {rows.map((r) => (
        <div key={r.id} className="border-b border-ink-100 px-4 py-3 last:border-b-0">
          <div
            className={r.gone ? 'text-body text-ink-300 line-through' : 'text-body text-ink-900'}
          >
            {r.text}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-meta text-ink-500">
            <span>{r.meta}</span>
            {r.gone ? <span>Withdrawn</span> : null}
            <span className="flex-1" />
            {r.untagged ? <span>Not tagged to an area</span> : null}
            {r.number ? <span className="font-mono text-ink-700">{r.number}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

function citationText(c: PersonDetail['evidence'][number]['citation']): string {
  if (c.interview_code) {
    if (c.at_ms != null) {
      const s = Math.floor(c.at_ms / 1000);
      const t = [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
        .map((n) => String(n).padStart(2, '0'))
        .join(':');
      return `${c.interview_code} at ${t}`;
    }
    return c.paragraph_no != null
      ? `${c.interview_code} notes, paragraph ${c.paragraph_no}`
      : c.interview_code;
  }
  if (c.question_code) return c.question_code;
  return c.kind;
}

function attachedText(d: PersonDetail): string {
  const a = d.attached;
  return (
    [
      a.interviews && plural(a.interviews, 'interview'),
      a.answers && plural(a.answers, 'answer'),
      a.evidence && plural(a.evidence, 'evidence item'),
      a.speaker_mappings && plural(a.speaker_mappings, 'transcript speaker'),
      a.follow_ups && plural(a.follow_ups, 'follow-up'),
      a.department_rounds && 'a department questionnaire',
      a.open_links && plural(a.open_links, 'open secure link'),
    ]
      .filter(Boolean)
      .join(', ') || 'something attached'
  );
}
